import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Menu,
  ListItemIcon,
  Checkbox,
  Toolbar,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  People,
  Warning,
  Visibility,
  Search,
  MoreVert,
  Block,
  CheckCircle,
  Download,
  Refresh,
  PersonOff,
  Subscriptions,
  TrendingUp,
  Delete,
  DeleteForever,
  SelectAll,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import { useSnackbar } from 'notistack';
import { addUserDailySpeechCredits, getAdminUsers, getUserSpeechUsage } from '../services/adminApi';

const UserManagement = () => {
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  // User Overview Data
  const [userStats, setUserStats] = useState({});
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const userFetchRequest = useRef(0);

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubscription, setFilterSubscription] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // User Details
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [userActivity, setUserActivity] = useState([]);
  const [userTransactions, setUserTransactions] = useState([]);
  const [speechUsage, setSpeechUsage] = useState(null);
  const [speechCreditsDialogOpen, setSpeechCreditsDialogOpen] = useState(false);
  const [speechCreditsInput, setSpeechCreditsInput] = useState('');

  // User Actions
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);

  // Delete functionality
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState('soft'); // 'soft' or 'permanent'
  const [deletingUser, setDeletingUser] = useState(null);

  // Duplicate Users (legacy)
  const [duplicates, setDuplicates] = useState([]);

  // Professional Cache Management
  const [cacheStatus, setCacheStatus] = useState({});
  const [cacheRefreshing, setCacheRefreshing] = useState(false);

  // Fetch cache status
  const fetchCacheStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/cache/status`);
      setCacheStatus(response.data);
    } catch (error) {
      console.error('Cache status fetch error:', error);
    }
  }, []);

  // Clear all caches
  const clearAllCaches = async () => {
    try {
      setCacheRefreshing(true);
      const response = await axios.post(`${API_BASE_URL}/api/admin/cache/clear-all`, {});

      enqueueSnackbar('✅ ' + response.data.message, { variant: 'success', autoHideDuration: 3000 });

      // Refresh all data after cache clear
      setTimeout(async () => {
        await Promise.all([
          fetchUserData(),
          fetchAllUsers(),
          fetchCacheStatus()
        ]);
        setCacheRefreshing(false);
      }, 1000);

    } catch (error) {
      enqueueSnackbar('❌ Failed to clear caches: ' + (error.response?.data?.error || error.message),
        { variant: 'error', autoHideDuration: 4000 });
      setCacheRefreshing(false);
    }
  };

  // Force refresh all data
  const forceRefreshData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchUserData(),
        fetchAllUsers(),
        fetchCacheStatus()
      ]);
      enqueueSnackbar('✅ Data refreshed successfully', { variant: 'success', autoHideDuration: 3000 });
    } catch (error) {
      enqueueSnackbar('❌ Failed to refresh data', { variant: 'error', autoHideDuration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  // Subscription management
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptionAction, setSubscriptionAction] = useState('set');
  const [subscriptionPlan, setSubscriptionPlan] = useState('pro');
  const [subscriptionMonths, setSubscriptionMonths] = useState(1);
  const [subscriptionReason, setSubscriptionReason] = useState('');
  const [subscriptionTimeUnit, setSubscriptionTimeUnit] = useState('months');
  const [subscriptionDuration, setSubscriptionDuration] = useState(1);

  // Quick test presets for professional testing
  const testPresets = [
    { label: '1 Minute (Testing)', duration: 1, unit: 'minutes' },
    { label: '5 Minutes (Testing)', duration: 5, unit: 'minutes' },
    { label: '30 Minutes (Testing)', duration: 30, unit: 'minutes' },
    { label: '1 Hour (Testing)', duration: 1, unit: 'hours' },
    { label: '1 Day (Testing)', duration: 1, unit: 'days' },
    { label: '1 Week (Testing)', duration: 1, unit: 'weeks' },
    { label: '1 Month (Production)', duration: 1, unit: 'months' },
    { label: '3 Months (Production)', duration: 3, unit: 'months' },
    { label: '6 Months (Production)', duration: 6, unit: 'months' },
    { label: '12 Months (Production)', duration: 12, unit: 'months' },
  ];

  // Convert duration to months for backend compatibility
  const convertToMonths = (duration, unit) => {
    const conversions = {
      minutes: duration / (60 * 24 * 30), // Very small fraction
      hours: duration / (24 * 30),
      days: duration / 30,
      weeks: duration / 4.33, // Average weeks per month
      months: duration
    };
    return conversions[unit] || duration;
  };

  // Apply test preset
  const applyTestPreset = (preset) => {
    setSubscriptionDuration(preset.duration);
    setSubscriptionTimeUnit(preset.unit);
    setSubscriptionMonths(convertToMonths(preset.duration, preset.unit));
  };

  // Fetch comprehensive user statistics
  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/user-stats`);
      setUserStats(response.data);
    } catch (error) {
      console.error('User stats fetch error:', error);

      // If quota exceeded, use mock data for testing
      if (error.response?.status === 500) {
        setUserStats({
          totalUsers: 127,
          activeUsers: 89,
          proSubscribers: 23,
          premiumSubscribers: 8,
          suspendedUsers: 2,
          userGrowthRate: 15,
          newUsersThisWeek: 12,
          statsGeneratedAt: new Date().toISOString()
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all users with detailed information
  const fetchAllUsers = useCallback(async () => {
    const requestId = ++userFetchRequest.current;
    try {
      setLoading(true);
      const response = await getAdminUsers(searchTerm);
      if (requestId === userFetchRequest.current) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Users fetch error:', error);

      // If quota exceeded, use mock data for testing
      if (requestId === userFetchRequest.current && error.response?.status === 500) {
        const mockUsers = [
          {
            id: 'user1',
            email: 'john.doe@example.com',
            username: 'johndoe',
            subscriptionPlan: 'pro',
            subscriptionExpiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            subscriptionExpired: false,
            createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
            lastLoginAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            suspended: false,
            totalSpent: 10000,
            totalTransactions: 3,
            language: 'en'
          },
          {
            id: 'user2',
            email: 'jane.smith@example.com',
            username: 'janesmith',
            subscriptionPlan: 'free',
            subscriptionExpiry: null,
            subscriptionExpired: false,
            createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            lastLoginAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            suspended: false,
            totalSpent: 800,
            totalTransactions: 2,
            language: 'en'
          },
          {
            id: 'user3',
            email: 'suspended.user@example.com',
            username: 'suspendeduser',
            subscriptionPlan: 'free',
            subscriptionExpiry: null,
            subscriptionExpired: false,
            createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            lastLoginAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
            suspended: true,
            totalSpent: 0,
            totalTransactions: 0,
            language: 'en'
          },
          {
            id: 'user4',
            email: 'inactive.user@example.com',
            username: 'inactiveuser',
            subscriptionPlan: 'free',
            subscriptionExpiry: null,
            subscriptionExpired: false,
            createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
            lastLoginAt: null,
            suspended: false,
            totalSpent: 400,
            totalTransactions: 1,
            language: 'en'
          },
          {
            id: 'user5',
            email: 'premium.user@example.com',
            username: 'premiumuser',
            subscriptionPlan: 'premium',
            subscriptionExpiry: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(),
            subscriptionExpired: false,
            createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
            lastLoginAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            suspended: false,
            totalSpent: 25000,
            totalTransactions: 5,
            language: 'en'
          }
        ];
        setUsers(mockUsers);
      }
    } finally {
      if (requestId === userFetchRequest.current) setLoading(false);
    }
  }, [searchTerm]);

  // Legacy duplicate users functionality
  const fetchDuplicateUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/duplicate-users`);
      setDuplicates(response.data.duplicates || []);
    } catch (error) {
      console.error('Duplicate users fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserData();
    fetchCacheStatus();
    if (tabValue === 2) fetchDuplicateUsers();
  }, [tabValue, fetchUserData, fetchDuplicateUsers, fetchCacheStatus]);

  useEffect(() => {
    if (tabValue !== 0) return undefined;
    const timeout = setTimeout(fetchAllUsers, searchTerm.trim() ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [fetchAllUsers, searchTerm, tabValue]);

  // Fetch detailed user information
  const fetchUserDetails = useCallback(async (userId) => {
    try {
      setSpeechUsage(null);
      const [userRes, activityRes, transactionsRes, speechUsageRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/users/${userId}`),
        axios.get(`${API_BASE_URL}/api/admin/users/${userId}/activity`),
        axios.get(`${API_BASE_URL}/api/admin/users/${userId}/transactions`),
        getUserSpeechUsage(userId),
      ]);

      setSelectedUser(userRes.data);
      setUserActivity(activityRes.data.activity || []);
      setUserTransactions(transactionsRes.data.transactions || []);
      setSpeechUsage(speechUsageRes.data.usage);
    } catch (error) {
      console.error('User details fetch error:', error);
    }
  }, []);

  // Apply search and filters
  const applyFilters = useCallback(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => {
        switch (filterStatus) {
          case 'active': return !user.suspended && user.lastLoginAt;
          case 'suspended': return user.suspended;
          case 'inactive': return !user.lastLoginAt;
          default: return true;
        }
      });
    }

    // Subscription filter
    if (filterSubscription !== 'all') {
      filtered = filtered.filter(user => {
        switch (filterSubscription) {
          case 'pro': return user.subscriptionPlan === 'pro';
          case 'premium': return user.subscriptionPlan === 'premium';
          case 'free': return !user.subscriptionPlan || user.subscriptionPlan === 'free';
          case 'expired': return user.subscriptionExpired;
          default: return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'createdAt' || sortBy === 'lastLoginAt') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterStatus, filterSubscription, sortBy, sortOrder]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // User action handlers
  const handleSuspendUser = async (userId, suspend) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/users/${userId}/suspend`,
        { suspend }
      );

      enqueueSnackbar(
        `✅ User ${suspend ? 'suspended' : 'unsuspended'} successfully`,
        { variant: 'success', autoHideDuration: 3000 }
      );
      fetchAllUsers();
    } catch (error) {
      console.error('Suspend user error:', error);
      enqueueSnackbar(
        `❌ Failed to ${suspend ? 'suspend' : 'unsuspend'} user: ${error.response?.data?.error || error.message}`,
        { variant: 'error', autoHideDuration: 4000 }
      );
    }
  };

  const openSpeechCreditsDialog = () => {
    setSpeechCreditsInput('');
    setSpeechCreditsDialogOpen(true);
  };

  const handleAddSpeechCredits = async () => {
    if (!selectedUser) return;
    const credits = Number(speechCreditsInput.trim());
    if (!Number.isSafeInteger(credits) || credits < 1) {
      enqueueSnackbar('Enter a positive whole number.', { variant: 'error' });
      return;
    }
    try {
      const response = await addUserDailySpeechCredits(selectedUser.id, credits);
      setSpeechUsage(response.data.usage);
      setSpeechCreditsDialogOpen(false);
      enqueueSnackbar('Daily speech credits added.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Daily speech credits were not added.', {
        variant: 'error',
      });
    }
  };

  // Handle user deletion
  const handleDeleteUser = useCallback(async (userId, permanent = false) => {
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}?permanent=${permanent}`);

      enqueueSnackbar(`✅ User ${permanent ? 'permanently' : 'soft'} deleted successfully`,
        { variant: 'success', autoHideDuration: 3000 });
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      fetchAllUsers();
      fetchUserData();
    } catch (error) {
      enqueueSnackbar('❌ Failed to delete user', { variant: 'error', autoHideDuration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [fetchAllUsers, fetchUserData, enqueueSnackbar]);

  // Handle bulk user deletion
  const handleBulkDelete = useCallback(async () => {
    if (selectedUsers.length === 0) return;

    try {
      setLoading(true);
      await axios.post(`${API_BASE_URL}/api/admin/users/bulk-delete`, {
        userIds: selectedUsers,
        permanent: deleteType === 'permanent'
      });

      enqueueSnackbar(`✅ ${selectedUsers.length} users ${deleteType === 'permanent' ? 'permanently' : 'soft'} deleted successfully`,
        { variant: 'success', autoHideDuration: 3000 });
      setDeleteDialogOpen(false);
      setSelectedUsers([]);
      fetchAllUsers();
      fetchUserData();
    } catch (error) {
      enqueueSnackbar('❌ Failed to bulk delete users', { variant: 'error', autoHideDuration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [selectedUsers, deleteType, fetchAllUsers, fetchUserData, enqueueSnackbar]);

  // Open delete confirmation dialog
  const openDeleteDialog = useCallback((user = null, isBulk = false) => {
    if (isBulk && selectedUsers.length === 0) {
      enqueueSnackbar('❌ No users selected for deletion', { variant: 'error', autoHideDuration: 4000 });
      return;
    }
    setDeletingUser(user);
    setDeleteDialogOpen(true);
  }, [selectedUsers, enqueueSnackbar]);

  // Toggle user selection for bulk operations
  const toggleUserSelection = useCallback((userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  }, []);

  // Professional Cache Management Functions
  const refreshCache = useCallback(async (scope = 'users', reason = 'Manual admin refresh') => {
    try {
      setCacheRefreshing(true);

      await axios.post(`${API_BASE_URL}/api/admin/cache/invalidate`, {
        scope,
        reason
      });

      // Force fresh data fetch
      await Promise.all([
        fetchAllUsers(),
        fetchUserData()
      ]);

      enqueueSnackbar('✅ Cache refreshed successfully - showing latest data',
        { variant: 'success', autoHideDuration: 3000 });
    } catch (error) {
      enqueueSnackbar(`❌ Failed to refresh cache: ${error.response?.data?.error || error.message}`,
        { variant: 'error', autoHideDuration: 4000 });
    } finally {
      setCacheRefreshing(false);
    }
  }, [fetchAllUsers, fetchUserData, enqueueSnackbar]);



  // Select all users
  const toggleSelectAll = useCallback(() => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  }, [selectedUsers, users]);

  // Open user details dialog
  const openUserDetails = useCallback((user) => {
    setSelectedUser(user);
    fetchUserDetails(user.id);
    setUserDetailsOpen(true);
  }, [fetchUserDetails]);

  // Add subscription management handler after handleDeleteUser function
  const handleSubscriptionManagement = async () => {
    if (!selectedUser || !subscriptionAction) return;

    try {
      setLoading(true);

      // Calculate final months value based on time unit
      const finalMonths = convertToMonths(subscriptionDuration, subscriptionTimeUnit);

      const payload = {
        action: subscriptionAction,
        reason: subscriptionReason || `Admin ${subscriptionAction} subscription via dashboard`
      };

      if (subscriptionAction === 'set') {
        if (!subscriptionPlan) {
          enqueueSnackbar('❌ Plan is required for setting subscription', { variant: 'error', autoHideDuration: 4000 });
          return;
        }
        if (!subscriptionDuration || subscriptionDuration <= 0) {
          enqueueSnackbar('❌ Duration must be a positive number', { variant: 'error', autoHideDuration: 4000 });
          return;
        }
        payload.plan = subscriptionPlan;
        payload.months = finalMonths;

        console.log(`Setting ${subscriptionPlan} subscription for ${subscriptionDuration} ${subscriptionTimeUnit} (${finalMonths} months)`);
      } else if (subscriptionAction === 'extend') {
        if (!subscriptionDuration || subscriptionDuration <= 0) {
          enqueueSnackbar('❌ Duration must be a positive number', { variant: 'error', autoHideDuration: 4000 });
          return;
        }
        payload.months = finalMonths;

        console.log(`Extending subscription by ${subscriptionDuration} ${subscriptionTimeUnit} (${finalMonths} months)`);
      }

      await axios.post(`${API_BASE_URL}/api/admin/users/${selectedUser.id}/subscription`, payload);

      enqueueSnackbar(`✅ Subscription ${subscriptionAction} completed successfully`,
        { variant: 'success', autoHideDuration: 3000 });
      setSubscriptionDialogOpen(false);

      // Reset form states
      setSubscriptionReason('');
      setSubscriptionDuration(1);
      setSubscriptionTimeUnit('months');
      setSubscriptionMonths(1);

      // PROFESSIONAL: Force cache refresh and fresh data fetch
      await refreshCache('users', `Post-${subscriptionAction} refresh`);

      if (userDetailsOpen) {
        await fetchUserDetails(selectedUser.id);
      }
    } catch (error) {
      enqueueSnackbar(`❌ Failed to ${subscriptionAction} subscription: ${error.response?.data?.error || error.message}`,
        { variant: 'error', autoHideDuration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  // Statistics Card Component
  const StatCard = ({ title, value, color, icon, subtitle, trend }) => (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {value?.toLocaleString()}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                <TrendingUp fontSize="small" color={trend > 0 ? "success" : "error"} />
                <Typography variant="caption" color={trend > 0 ? "success.main" : "error.main"}>
                  {trend > 0 ? '+' : ''}{trend}%
                </Typography>
              </Box>
            )}
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Column definition for DataGrid
  const columns = [
    {
      field: 'select',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          checked={selectedUsers.length > 0 && selectedUsers.length === users.length}
          indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
          onChange={toggleSelectAll}
          size="small"
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedUsers.includes(params.row.id)}
          onChange={() => toggleUserSelection(params.row.id)}
          size="small"
        />
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
          {params.row.suspended && <Block sx={{ color: 'red', fontSize: 16 }} />}
        </Box>
      ),
    },
    {
      field: 'username',
      headerName: 'Username',
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: 'subscriptionPlan',
      headerName: 'Plan',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={params.value || 'Free'}
            color={params.value === 'pro' ? 'primary' : params.value === 'premium' ? 'secondary' : 'default'}
            size="small"
          />
          {params.row.realtimeStatus && (
            <Tooltip title="Real-time status verified">
              <CheckCircle sx={{ fontSize: 12, color: 'success.main' }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => {
        let status = 'Active';
        let color = 'success';

        if (params.row.suspended) {
          status = 'Suspended';
          color = 'error';
        } else if (params.row.subscriptionExpired) {
          status = 'Expired';
          color = 'warning';
        } else if (!params.row.lastLoginAt) {
          status = 'Inactive';
          color = 'default';
        }

        return <Chip label={status} color={color} size="small" />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Join Date',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : 'N/A',
    },
    {
      field: 'lastLoginAt',
      headerName: 'Last Login',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : 'Never',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => openUserDetails(params.row)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete User">
            <IconButton
              size="small"
              color="error"
              onClick={() => openDeleteDialog(params.row)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="More Actions">
            <IconButton
              size="small"
              onClick={(e) => {
                setSelectedUser(params.row);
                setActionMenuAnchor(e.currentTarget);
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading && tabValue === 0 && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
        User Management
      </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => {/* TODO: Export users */}}
          >
            Export Users
          </Button>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            onClick={forceRefreshData}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Warning />}
            onClick={fetchCacheStatus}
          >
            Cache Status
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={cacheRefreshing ? <CircularProgress size={16} /> : <DeleteForever />}
            onClick={clearAllCaches}
            disabled={cacheRefreshing}
          >
            {cacheRefreshing ? 'Clearing...' : 'Clear All Caches'}
          </Button>
        </Box>
      </Box>

      {/* User Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={userStats.totalUsers}
            color="primary.main"
            icon={<People sx={{ fontSize: 40 }} />}
            subtitle="All registered users"
            trend={userStats.userGrowthRate}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Users"
            value={userStats.activeUsers}
            color="success.main"
            icon={<CheckCircle sx={{ fontSize: 40 }} />}
            subtitle="Active in last 7 days"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Pro Subscribers"
            value={userStats.proSubscribers}
            color="info.main"
            icon={<Subscriptions sx={{ fontSize: 40 }} />}
            subtitle="Active Pro plans"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Premium Subscribers"
            value={userStats.premiumSubscribers}
            color="secondary.main"
            icon={<Subscriptions sx={{ fontSize: 40 }} />}
            subtitle="Active Premium plans"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Suspended Users"
            value={userStats.suspendedUsers}
            color={userStats.suspendedUsers > 0 ? "error.main" : "success.main"}
            icon={<PersonOff sx={{ fontSize: 40 }} />}
            subtitle="Require attention"
          />
        </Grid>
      </Grid>

      {/* Quota Notice */}
      {users.length > 0 && users[0].id === 'user1' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6">Demo Mode - Firestore Quota Exceeded</Typography>
          <Typography variant="body2">
            Showing mock data due to Firestore quota limits. Real data will be available when quota resets (24 hours) or you can upgrade your Firebase plan.
          </Typography>
        </Alert>
      )}



      {/* Professional Cache Status Display */}
      {Object.keys(cacheStatus).length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          onClose={() => setCacheStatus({})}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={clearAllCaches}
              disabled={cacheRefreshing}
            >
              Clear Cache
            </Button>
          }
        >
          <Typography variant="h6">Professional Cache Status</Typography>
          <Typography variant="body2">
            • Total Cache Keys: {cacheStatus.totalCacheKeys}
            <br />
            • User-Related Keys: {cacheStatus.userRelatedKeys}
            <br />
            • Has Fresh User Data: {cacheStatus.cacheInfo?.hasUserList ? '✅ Yes' : '❌ No'}
            <br />
            • Has Fresh Stats: {cacheStatus.cacheInfo?.hasUserStats ? '✅ Yes' : '❌ No'}
            <br />
            <strong>💡 Professional Tip:</strong> Cache automatically invalidates when subscriptions expire or admins make changes
          </Typography>
        </Alert>
      )}

      {/* Professional Real-Time Sync Status */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="h6">✅ Real-Time Subscription Sync Active</Typography>
        <Typography variant="body2">
          <strong>Professional Mode:</strong> Admin dashboard now shows real-time subscription status.
          <br />
          • Subscription expiry checked for each user on every load
          <br />
          • Expired subscriptions automatically downgraded in database
          <br />
          • Green checkmarks (✓) indicate real-time verified status
          <br />
          • No more cache mismatches between Flutter app and admin dashboard
        </Typography>
      </Alert>

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="All Users" icon={<People />} />
          <Tab label="Analytics" icon={<TrendingUp />} />
          <Tab label="Duplicates" icon={<Warning />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Paper elevation={2}>
          {/* Search and Filters */}
          <Box p={2} borderBottom="1px solid #e0e0e0">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Enter exact email or user ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Subscription</InputLabel>
                  <Select
                    value={filterSubscription}
                    onChange={(e) => setFilterSubscription(e.target.value)}
                    label="Subscription"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="premium">Premium</MenuItem>
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sort By"
                  >
                    <MenuItem value="createdAt">Join Date</MenuItem>
                    <MenuItem value="lastLoginAt">Last Login</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Order</InputLabel>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    label="Order"
                  >
                    <MenuItem value="desc">Descending</MenuItem>
                    <MenuItem value="asc">Ascending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Bulk Actions Toolbar */}
          {selectedUsers.length > 0 && (
            <Toolbar
              sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                bgcolor: 'rgba(25, 118, 210, 0.12)',
                borderRadius: 1,
                mb: 2
              }}
            >
              <Typography
                sx={{ flex: '1 1 100%' }}
                color="inherit"
                variant="subtitle1"
                component="div"
              >
                {selectedUsers.length} user(s) selected
              </Typography>

              <Tooltip title="Delete selected users">
                <IconButton
                  color="error"
                  onClick={() => openDeleteDialog(null, true)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>

              <Tooltip title="Clear selection">
                <IconButton onClick={() => setSelectedUsers([])}>
                  <SelectAll />
                </IconButton>
              </Tooltip>
            </Toolbar>
          )}

          {/* Users Table */}
          <Box sx={{
            height: { xs: 400, sm: 500, md: 600 },
            width: '100%',
            '& .MuiDataGrid-root': {
              border: 'none',
            }
          }}>
            <DataGrid
              rows={filteredUsers}
              columns={columns}
              pageSize={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageSizeChange={(newPageSize) => setRowsPerPage(newPageSize)}
              loading={loading}
              disableSelectionOnClick
              onRowDoubleClick={(params) => {
                setSelectedUser(params.row);
                fetchUserDetails(params.row.id);
                setUserDetailsOpen(true);
              }}
              getRowClassName={(params) =>
                params.row.suspended ? 'suspended-user' : ''
              }
              sx={{
                '& .suspended-user': {
                  backgroundColor: 'rgba(244, 67, 54, 0.05)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            User Analytics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Registration Trends
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Coming soon - Chart showing user registration over time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Activity Patterns
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Coming soon - User activity and engagement metrics
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {tabValue === 2 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Duplicate Users Management
          </Typography>

          {/* Legacy duplicate users functionality would go here */}
      {duplicates.length === 0 ? (
            <Alert severity="success">
          <Typography variant="h6">No duplicate users found</Typography>
          <Typography variant="body2">
            All users have unique email addresses. The system is clean.
          </Typography>
        </Alert>
      ) : (
            <Alert severity="warning">
          <Typography variant="h6">
            {duplicates.length} emails have duplicate users
          </Typography>
          <Typography variant="body2">
            These users need to be consolidated to prevent payment allocation issues.
          </Typography>
        </Alert>
      )}
        </Paper>
      )}

      {/* User Details Dialog */}
      <Dialog
        open={userDetailsOpen}
        onClose={() => setUserDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          User Details - {selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Profile Information</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="User ID" secondary={selectedUser.id} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Email" secondary={selectedUser.email} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Username" secondary={selectedUser.username || 'Not set'} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Subscription" secondary={selectedUser.subscriptionPlan || 'Free'} />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Status"
                          secondary={
                            <Chip
                              label={selectedUser.suspended ? 'Suspended' : 'Active'}
                              color={selectedUser.suspended ? 'error' : 'success'}
                              size="small"
                            />
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Speech Credits</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Daily limit"
                          secondary={speechUsage?.dailyLimit === -1
                            ? 'Unlimited'
                            : speechUsage?.dailyLimit ?? 'Not available'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Daily used"
                          secondary={speechUsage?.dailyUsed ?? 'Not available'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Daily remaining"
                          secondary={speechUsage?.dailyRemaining === -1
                            ? 'Unlimited'
                            : speechUsage?.dailyRemaining ?? 'Not available'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Rewarded credits"
                          secondary={speechUsage?.rewardedCredits ?? 'Not available'}
                        />
                      </ListItem>
                    </List>
                    <Button variant="outlined" onClick={openSpeechCreditsDialog}>
                      Add Daily Credits
                    </Button>
                  </CardContent>
                </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Recent Activity</Typography>
                  <List dense>
                      {userActivity.slice(0, 5).map((activity, index) => (
                      <ListItem key={index}>
                          <ListItemText
                            primary={activity.action}
                            secondary={new Date(activity.timestamp).toLocaleString()}
                          />
                      </ListItem>
                    ))}
                  </List>
                  </CardContent>
                </Card>
                </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Transaction History</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userTransactions.slice(0, 10).map((transaction, index) => (
                            <TableRow key={index}>
                              <TableCell>{new Date(transaction.timestamp).toLocaleDateString()}</TableCell>
                              <TableCell>{transaction.type}</TableCell>
                              <TableCell>₦{(transaction.amount / 100).toLocaleString()}</TableCell>
                              <TableCell>
                                <Chip
                                  label={transaction.status}
                                  color={transaction.status === 'completed' ? 'success' : 'warning'}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDetailsOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => setSubscriptionDialogOpen(true)}
            startIcon={<Subscriptions />}
            color="primary"
            sx={{ ml: 1 }}
          >
            Manage Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          if (selectedUser) {
            fetchUserDetails(selectedUser.id);
            setUserDetailsOpen(true);
          }
          setActionMenuAnchor(null);
        }}>
          <ListItemIcon><Visibility /></ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedUser) {
            handleSuspendUser(selectedUser.id, !selectedUser.suspended);
          }
          setActionMenuAnchor(null);
        }}>
          <ListItemIcon>
            {selectedUser?.suspended ? <CheckCircle /> : <Block />}
          </ListItemIcon>
          {selectedUser?.suspended ? 'Unsuspend' : 'Suspend'} User
        </MenuItem>
        <MenuItem onClick={() => {
          setSubscriptionDialogOpen(true);
          setActionMenuAnchor(null);
        }}>
          <ListItemIcon><Subscriptions /></ListItemIcon>
          Manage Subscription
        </MenuItem>
      </Menu>

      {/* Subscription Management Dialog */}
      <Dialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Subscription - {selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={subscriptionAction}
                onChange={(e) => setSubscriptionAction(e.target.value)}
                label="Action"
              >
                <MenuItem value="set">Set New Subscription</MenuItem>
                <MenuItem value="extend">Extend Current Subscription</MenuItem>
                <MenuItem value="cancel">Cancel Subscription</MenuItem>
                <MenuItem value="reset">Reset to Free</MenuItem>
              </Select>
            </FormControl>

            {(subscriptionAction === 'set') && (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Plan</InputLabel>
                  <Select
                    value={subscriptionPlan}
                    onChange={(e) => setSubscriptionPlan(e.target.value)}
                    label="Plan"
                  >
                    <MenuItem value="pro">Pro (₦10,000/month)</MenuItem>
                    <MenuItem value="premium">Premium (₦25,000/month)</MenuItem>
                  </Select>
                </FormControl>

                {/* Quick Test Presets */}
                <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>
                  Quick Test Presets:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {testPresets.map((preset, index) => (
                    <Chip
                      key={index}
                      label={preset.label}
                      onClick={() => applyTestPreset(preset)}
                      color={preset.unit === 'minutes' || preset.unit === 'hours' ? 'warning' : 'primary'}
                      variant="outlined"
                      size="small"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>

                {/* Custom Duration Input */}
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Custom Duration:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="Duration"
                    type="number"
                    value={subscriptionDuration}
                    onChange={(e) => {
                      const duration = parseInt(e.target.value) || 1;
                      setSubscriptionDuration(duration);
                      setSubscriptionMonths(convertToMonths(duration, subscriptionTimeUnit));
                    }}
                    inputProps={{ min: 1, max: subscriptionTimeUnit === 'minutes' ? 60 : subscriptionTimeUnit === 'hours' ? 24 : 365 }}
                    sx={{ flex: 1 }}
                  />
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Time Unit</InputLabel>
                    <Select
                      value={subscriptionTimeUnit}
                      onChange={(e) => {
                        setSubscriptionTimeUnit(e.target.value);
                        setSubscriptionMonths(convertToMonths(subscriptionDuration, e.target.value));
                      }}
                      label="Time Unit"
                    >
                      <MenuItem value="minutes">Minutes (Testing)</MenuItem>
                      <MenuItem value="hours">Hours (Testing)</MenuItem>
                      <MenuItem value="days">Days (Testing)</MenuItem>
                      <MenuItem value="weeks">Weeks (Testing)</MenuItem>
                      <MenuItem value="months">Months (Production)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Preview calculated duration */}
                <Alert
                  severity={subscriptionTimeUnit === 'minutes' || subscriptionTimeUnit === 'hours' ? 'warning' : 'info'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Subscription Duration:</strong> {subscriptionDuration} {subscriptionTimeUnit}
                    <br />
                    <strong>Calculated Value:</strong> {subscriptionMonths.toFixed(8)} months
                    {(subscriptionTimeUnit === 'minutes' || subscriptionTimeUnit === 'hours') && (
                      <>
                        <br />
                        <strong>⚠️ Testing Mode:</strong> This will expire quickly for testing purposes.
                      </>
                    )}
                  </Typography>
                </Alert>
              </>
            )}

            {subscriptionAction === 'extend' && (
              <>
                {/* Quick Test Presets for Extension */}
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Extend Duration:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {testPresets.map((preset, index) => (
                    <Chip
                      key={index}
                      label={`+${preset.label}`}
                      onClick={() => applyTestPreset(preset)}
                      color={preset.unit === 'minutes' || preset.unit === 'hours' ? 'warning' : 'primary'}
                      variant="outlined"
                      size="small"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>

                {/* Custom Extension Input */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="Extend by"
                    type="number"
                    value={subscriptionDuration}
                    onChange={(e) => {
                      const duration = parseInt(e.target.value) || 1;
                      setSubscriptionDuration(duration);
                      setSubscriptionMonths(convertToMonths(duration, subscriptionTimeUnit));
                    }}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                  />
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Time Unit</InputLabel>
                    <Select
                      value={subscriptionTimeUnit}
                      onChange={(e) => {
                        setSubscriptionTimeUnit(e.target.value);
                        setSubscriptionMonths(convertToMonths(subscriptionDuration, e.target.value));
                      }}
                      label="Time Unit"
                    >
                      <MenuItem value="minutes">Minutes (Testing)</MenuItem>
                      <MenuItem value="hours">Hours (Testing)</MenuItem>
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="weeks">Weeks</MenuItem>
                      <MenuItem value="months">Months</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Extension:</strong> +{subscriptionDuration} {subscriptionTimeUnit} ({subscriptionMonths.toFixed(8)} months)
                  </Typography>
                </Alert>
              </>
            )}

            {subscriptionAction === 'reset' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This will reset the user to the free plan.
                </Typography>
              </Alert>
            )}

            {subscriptionAction === 'cancel' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>⚠️ Professional SaaS Behavior:</strong>
                  <br />
                  • Subscription will be cancelled immediately
                  <br />
                  • All subscription benefits will be removed
                  <br />
                  • User will get 0 credits (clean slate policy)
                  <br />
                  • This action ensures no credit rollover abuse
                </Typography>
              </Alert>
            )}

            <TextField
              fullWidth
              label="Admin Reason"
              multiline
              rows={3}
              value={subscriptionReason}
              onChange={(e) => setSubscriptionReason(e.target.value)}
              placeholder="Reason for subscription modification (optional)..."
              helperText="This will be logged for audit purposes"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubscriptionManagement}
            variant="contained"
            disabled={loading}
            color={subscriptionAction === 'cancel' ? 'error' : 'primary'}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading
              ? 'Processing...'
              : `${subscriptionAction === 'set' ? 'Set' : subscriptionAction === 'extend' ? 'Extend' : subscriptionAction === 'cancel' ? 'Cancel' : 'Reset'} Subscription`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {selectedUsers.length > 1 || !deletingUser ? "Confirm Bulk User Deletion" : "Confirm User Deletion"}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {selectedUsers.length > 1 || !deletingUser
              ? `Are you sure you want to delete ${selectedUsers.length} selected users?`
              : `Are you sure you want to delete user "${deletingUser?.email}"?`
            }
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={deleteType === 'permanent'}
                onChange={(e) => setDeleteType(e.target.checked ? 'permanent' : 'soft')}
                color="error"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Permanent Deletion {deleteType === 'permanent' && <DeleteForever sx={{ color: 'red', ml: 1 }} />}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {deleteType === 'permanent'
                    ? 'All user data will be permanently removed from the system'
                    : 'User will be suspended and marked as deleted (can be recovered)'
                  }
                </Typography>
              </Box>
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (selectedUsers.length > 1 || !deletingUser) {
                handleBulkDelete();
              } else {
                handleDeleteUser(deletingUser.id, deleteType === 'permanent');
              }
            }}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={deleteType === 'permanent' ? <DeleteForever /> : <Delete />}
          >
            {loading ? <CircularProgress size={20} /> : ''}
            {deleteType === 'permanent' ? 'Permanently Delete' : 'Soft Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => {
          setActionMenuAnchor(null);
          setSelectedUser(null);
        }}
      >
        <MenuItem onClick={() => {
          setActionMenuAnchor(null);
          setSubscriptionDialogOpen(true);
        }}>
          <ListItemIcon>
            <Subscriptions fontSize="small" />
          </ListItemIcon>
          Manage Subscription
        </MenuItem>

        <MenuItem onClick={() => {
          setActionMenuAnchor(null);
          handleSuspendUser(selectedUser.id, !selectedUser.suspended);
        }}>
          <ListItemIcon>
            <Block fontSize="small" />
          </ListItemIcon>
          {selectedUser?.suspended ? 'Unsuspend User' : 'Suspend User'}
        </MenuItem>
      </Menu>

      <Dialog
        open={speechCreditsDialogOpen}
        onClose={() => setSpeechCreditsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Daily Speech Credits</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Credits to add"
            type="number"
            value={speechCreditsInput}
            onChange={(event) => setSpeechCreditsInput(event.target.value)}
            helperText={`Current daily limit: ${speechUsage?.dailyLimit ?? 'Not available'}. Credits expire at the next daily reset.`}
            inputProps={{ min: 1, step: 1 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpeechCreditsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSpeechCredits}>Add Credits</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
